import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface InlineEditableSelectProps {
  value: string;
  options: string[];
  onSave: (value: string) => Promise<void>;
  getOptionLabel?: (option: string) => string;
  groupBy?: (option: string) => string;
  disabled?: boolean;
  fontSize?: string | number;
  fontWeight?: number;
}

// Initial and incremental page size for virtual pagination
const PAGE_SIZE = 100;

/**
 * Same visual pattern as InlineEditableText but with a search/dropdown.
 *
 *  - Display: read-only, hover bg, click → opens.
 *  - Edit: editable search, popover list opens below.
 *  - Pick option → save; blur/Escape → cancel.
 *
 * The dropdown defers rendering for two animation frames so the click feels
 * instant; thereafter loads pages of `PAGE_SIZE` via IntersectionObserver
 * to keep large option lists (e.g. all IANA timezones) responsive.
 */
const InlineEditableSelect: React.FC<InlineEditableSelectProps> = ({
  value,
  options,
  onSave,
  getOptionLabel,
  groupBy,
  disabled = false,
  fontSize = '1rem',
  fontWeight = 400,
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownReady, setDropdownReady] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [hlIndex, setHlIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [sentinelEl, setSentinelEl] = useState<HTMLLIElement | null>(null);

  const [inputWidth, setInputWidth] = useState(0);
  const [labelWidth, setLabelWidth] = useState(0);

  const displayLabel = getOptionLabel ? getOptionLabel(value) : value;
  const displayText = editing ? search : displayLabel;

  useLayoutEffect(() => {
    if (measureRef.current) {
      const w = measureRef.current.offsetWidth;
      if (w > 0) setInputWidth(w + 2);
    }
  }, [displayText]);

  useLayoutEffect(() => {
    if (!editing && measureRef.current) {
      setLabelWidth(measureRef.current.offsetWidth + 2);
    }
  }, [editing, displayLabel]);

  useEffect(() => {
    if (editing) {
      setSearch('');
      setVisibleCount(PAGE_SIZE);
      inputRef.current?.focus();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setDropdownReady(true));
      });
    }
  }, [editing]);

  useEffect(() => {
    setHlIndex(0);
    setVisibleCount(PAGE_SIZE);
  }, [search]);

  useEffect(() => {
    if (!sentinelEl || !popoverRef.current) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) setVisibleCount(c => c + PAGE_SIZE);
      },
      { root: popoverRef.current, threshold: 0 },
    );
    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [sentinelEl]);

  const { visibleOptions, totalCount } = useMemo(() => {
    if (!dropdownReady) return { visibleOptions: [] as string[], totalCount: 0 };
    let all: string[];
    if (!search) {
      all = options;
    } else {
      const q = search.toLowerCase();
      all = options.filter(opt => {
        const label = getOptionLabel ? getOptionLabel(opt) : opt;
        return label.toLowerCase().includes(q);
      });
    }
    return { visibleOptions: all.slice(0, visibleCount), totalCount: all.length };
  }, [dropdownReady, search, visibleCount, options, getOptionLabel]);

  const hasMore = visibleOptions.length < totalCount;

  const grouped = useMemo(() => {
    if (!groupBy) return [['', visibleOptions] as const];
    const map = new Map<string, string[]>();
    for (const opt of visibleOptions) {
      const g = groupBy(opt);
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(opt);
    }
    return [...map.entries()];
  }, [visibleOptions, groupBy]);

  function handleClick() {
    if (disabled || saving || editing) return;
    setEditing(true);
  }

  function handleCancel() {
    setSearch('');
    setEditing(false);
    setDropdownReady(false);
  }

  async function handleSelect(newValue: string) {
    setSearch('');
    setEditing(false);
    setDropdownReady(false);
    if (newValue === value) return;
    setSaving(true);
    try {
      await onSave(newValue);
    } finally {
      setSaving(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { handleCancel(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHlIndex(i => Math.min(i + 1, visibleOptions.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHlIndex(i => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter' && visibleOptions.length > 0) {
      e.preventDefault();
      void handleSelect(visibleOptions[hlIndex]);
      return;
    }
  }

  function handleBlur() {
    setTimeout(() => {
      if (editing && !saving && document.activeElement !== inputRef.current) {
        handleCancel();
      }
    }, 100);
  }

  const effectiveWidth = Math.max(inputWidth, labelWidth, 40);
  const sharedTextStyle: React.CSSProperties = {
    fontSize,
    fontWeight,
    fontFamily: 'inherit',
    letterSpacing: 'inherit',
    lineHeight: 'inherit',
    whiteSpace: 'pre',
  };
  const isMutedText = saving || disabled;

  return (
    <span className="relative inline-flex w-fit items-center gap-1">
      {/* Hidden measuring span */}
      <span
        ref={measureRef}
        aria-hidden="true"
        style={{
          ...sharedTextStyle,
          position: 'fixed',
          visibility: 'hidden',
          top: -9999,
          left: -9999,
          pointerEvents: 'none',
        }}
      >
        {displayText || ' '}
      </span>

      <div
        ref={anchorRef}
        onClick={handleClick}
        className={cn(
          'inline-flex items-center rounded-md px-1.5 py-0.5 transition-[background-color,box-shadow]',
          isMutedText ? 'text-muted-foreground' : 'text-foreground',
          editing ? 'cursor-text' : isMutedText ? 'cursor-default' : 'cursor-pointer',
          !editing && !disabled && !saving && 'hover:bg-muted hover:shadow-[inset_0_0_0_1px_var(--color-border)]',
        )}
      >
        <input
          ref={inputRef}
          value={displayText}
          onChange={e => editing && setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          readOnly={!editing || saving || disabled}
          placeholder={editing ? 'Поиск…' : undefined}
          aria-label="Выбор значения"
          style={{
            ...sharedTextStyle,
            cursor: editing && !saving && !disabled ? 'text' : 'inherit',
            caretColor: editing && !saving && !disabled ? 'auto' : 'transparent',
            userSelect: editing && !saving && !disabled ? 'auto' : 'none',
            color: 'inherit',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            padding: 0,
            margin: 0,
            boxSizing: 'content-box',
            width: `${effectiveWidth}px`,
            minWidth: 40,
          }}
        />
      </div>

      <button
        type="button"
        onClick={() => (editing ? handleCancel() : undefined)}
        disabled={saving}
        aria-label="Подтвердить"
        className={cn(
          'inline-flex size-7 items-center justify-center rounded-md text-primary transition-colors',
          'hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
          'disabled:opacity-60',
          !editing && !saving && 'invisible',
        )}
      >
        {saving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
      </button>

      {editing && (
        <div
          ref={popoverRef}
          className={cn(
            'absolute left-0 top-full z-50 mt-1 max-h-80 min-w-[22rem] overflow-auto',
            'rounded-lg border border-border bg-card text-card-foreground shadow-lg',
          )}
        >
          {!dropdownReady ? (
            <div className="flex justify-center p-3">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            </div>
          ) : visibleOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">Нет совпадений</div>
          ) : (
            <ul className="py-1">
              {grouped.map(([group, items]) => (
                <React.Fragment key={`g-${group || '_'}`}>
                  {group && (
                    <li
                      className="sticky top-0 z-10 bg-card/95 px-3 pb-0.5 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur"
                    >
                      {group}
                    </li>
                  )}
                  {items.map(opt => {
                    const idx = visibleOptions.indexOf(opt);
                    const highlighted = idx === hlIndex;
                    const selected = opt === value;
                    return (
                      <li key={opt}>
                        <button
                          type="button"
                          onMouseDown={e => { e.preventDefault(); void handleSelect(opt); }}
                          className={cn(
                            'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm',
                            highlighted ? 'bg-muted' : '',
                            selected ? 'font-medium text-primary' : 'text-foreground',
                          )}
                        >
                          <span className="truncate">{getOptionLabel ? getOptionLabel(opt) : opt}</span>
                          {selected && <Check className="size-4 shrink-0" />}
                        </button>
                      </li>
                    );
                  })}
                </React.Fragment>
              ))}
              {hasMore && (
                <li ref={setSentinelEl} className="h-2" />
              )}
            </ul>
          )}
        </div>
      )}
    </span>
  );
};

export default InlineEditableSelect;
